import { useEffect, useRef, useState } from 'react';
import { fetchAuthSession, signOut } from 'aws-amplify/auth';
import { clearAllStorage } from '../utils/authStorage';

const isTestEmail = (email: unknown): boolean =>
  typeof email === 'string' && email.startsWith('pozytron.test');

/**
 * Hook to automatically refresh Cognito tokens every 4 minutes.
 * Returns sessionReady=true once the initial token check completes,
 * so the app can block rendering until the session state is known.
 * Auto-logout is disabled for test accounts (pozytron.test+).
 */
export const useAutoTokenRefresh = () => {
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isTestAccountRef = useRef(false);
  const [sessionReady, setSessionReady] = useState(false);

  useEffect(() => {
    let mounted = true;

    const init = async () => {
      try {
        const cached = await fetchAuthSession({ forceRefresh: false });
        const email = cached.tokens?.idToken?.payload?.['email'];
        if (mounted) {
          isTestAccountRef.current = isTestEmail(email);
        }
      } catch {
        // No session yet — treat as non-test account
      }

      if (!mounted) {
        return;
      }

      const refreshToken = async () => {
        try {
          await fetchAuthSession({ forceRefresh: true });
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : 'Token refresh failed';

          if (isTestAccountRef.current) {
            if (intervalRef.current) {
              clearInterval(intervalRef.current);
              intervalRef.current = null;
            }
            return;
          }

          if (
            errorMessage.includes('PreTokenGeneration failed') ||
            errorMessage.includes('dezaktywowane') ||
            errorMessage.includes('deactivated') ||
            errorMessage.includes('User is disabled')
          ) {
            if (intervalRef.current) {
              clearInterval(intervalRef.current);
              intervalRef.current = null;
            }
            await signOut();
            await clearAllStorage();
            localStorage.setItem('logout-event', Date.now().toString());
            localStorage.setItem('logout-reason', 'ACCOUNT_DEACTIVATED');
            window.location.href = '/login';
            return;
          }

          if (
            errorMessage.includes('NotAuthorizedException') ||
            errorMessage.includes('Refresh Token has expired') ||
            errorMessage.includes('Token has been revoked') ||
            errorMessage.includes('Invalid Refresh Token') ||
            errorMessage.includes('User is not authorized')
          ) {
            if (intervalRef.current) {
              clearInterval(intervalRef.current);
              intervalRef.current = null;
            }
            await signOut();
            await clearAllStorage();
            localStorage.setItem('logout-event', Date.now().toString());
            window.location.href = '/login';
          }
        }
      };

      await refreshToken();

      if (mounted) {
        setSessionReady(true);
        intervalRef.current = setInterval(refreshToken, 4 * 60 * 1000);
      }
    };

    init();

    return () => {
      mounted = false;
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, []);

  return { sessionReady };
};
